import { ChevronRight, Lock } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { AuditLogsCounter } from "@/features/audit-logs/components/AuditLogsCounter";
import { BeneficiaryValidationCounter } from "@/features/beneficiaries/components/BeneficiaryValidationCounter";
import { ChatCounter } from "@/features/chat/components/ChatCounter";
import { useChatUnreadCount } from "@/features/chat/hooks/useChatUnreadCount";
import { CommunicationCounter } from "@/features/communication/components/CommunicationCounter";
import { FormSubmissionCounter } from "@/features/custom-forms/components/FormSubmissionCounter";
import { DiscussionCounter } from "@/features/discussions/components/DiscussionCounter";
import { EcomOrderCounter } from "@/features/ecom-orders/components/EcomOrderCounter";
import JobCounter from "@/features/jobs/components/JobCounter";
import JobFloatingIndicator from "@/features/jobs/components/JobFloatingIndicator";
import { useJobCounter } from "@/features/jobs/hooks/useJobCounter";
import { LegalQuestionUnreadCounter } from "@/features/legal-questions/components/LegalQuestionUnreadCounter";
import { RefundRequestCounter } from "@/features/refund-requests/components/RefundRequestCounter";
import { CatalogBookingsCounter } from "@/features/yeati-loc/components/CatalogBookingsCounter";
import { NotificationBadge } from "@/shared/components/NotificationBadge";
import { BADGE_GRADIENT_PREMIUM } from "@/shared/components/UpsellPage/constants/upsellPage.constants";
import type { PlanTier } from "@/shared/components/UpsellPage/types/upsellPage.types";
import { FILE_MANAGER_PATH } from "@/shared/configs/routes.config";
import { useAdminNotificationsContext } from "@/shared/contexts/AdminNotificationsProvider";
import { useOrganizationPlan } from "@/shared/hooks/useOrganizationFeatures";
import { cn } from "@/shared/lib/utils";
import type { AdminNotificationCounts } from "@/shared/types/adminNotifications.types";
import type { UpsellCategoryKey, UpsellMetadata } from "@/shared/types/common";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/shared/ui/Sidebar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/Tooltip";
import type { NavigationItem, NavigationSubItem } from "@/shared/utils/navigation.utils";
import { getUpsellMetadata } from "@/shared/utils/upsell.utils";

import FileTreeNavigation from "./FileTreeNavigation";
import { TruncatedSidebarLabel } from "./TruncatedSidebarLabel";

/**
 * Map of navigation item names to their counter component (for sub-items).
 */
const COUNTER_MAP: Record<string, React.FC> = {
  "Tâches": JobCounter,
  "Discussions": DiscussionCounter,
  "Formulaires": FormSubmissionCounter,
  "Historique": AuditLogsCounter,
  "Emails": CommunicationCounter,
  "Remboursements": RefundRequestCounter,
  "Bons Plans": EcomOrderCounter,
  "Réservations": CatalogBookingsCounter,
  "Chat": ChatCounter,
  "Ayants droit": BeneficiaryValidationCounter,
  "Questions juridiques": LegalQuestionUnreadCounter,
};

/**
 * Map of navigation item names to their notification count key.
 * Used to compute summed badges for parent items.
 */
const COUNT_KEY_MAP: Record<string, keyof AdminNotificationCounts> = {
  "Discussions": "discussions_a_traiter",
  "Formulaires": "submissions_pending",
  "Historique": "audit_logs_total",
  "Emails": "communication_unread",
  "Remboursements": "refund_requests_pending",
  "Bons Plans": "ecom_orders_pending",
  "Réservations": "catalog_bookings_pending",
  "Petites Annonces": "classified_ads_pending",
  "Ayants droit": "beneficiaries_pending_validation",
  "Justificatifs": "proof_documents_pending",
  "Questions juridiques": "legal_questions_unread",
};

/** Render counter for a single sub-item */
const renderSubItemCounter = (item: NavigationSubItem) => {
  const Counter = COUNTER_MAP[item.name];
  return Counter ? <Counter key={item.name} /> : null;
};

/** Un parent contient-il le sous-item « Tâches » (tâches async) ? */
const parentHasJobSubItem = (item: NavigationItem): boolean =>
  item.items?.some((sub) => sub.name === "Tâches") ?? false;

const LOCKED_CONTENT_CLASS = "[&>svg]:opacity-60 [&>span:not([data-slot=badge])]:opacity-60 hover:[&>svg]:opacity-90 hover:[&>span:not([data-slot=badge])]:opacity-90 transition-all";

const ProBadge = ({ tier = "standard" }: { tier?: PlanTier }) => {
  const isPremium = tier === "premium";
  return (
    <span
      aria-label={isPremium ? "Premium" : "Standard"}
      className="ml-auto flex size-5 shrink-0 items-center justify-center rounded-full text-white"
      style={{
        background: isPremium
          ? BADGE_GRADIENT_PREMIUM
          : "linear-gradient(135deg, #8775f9, #a78bfa)",
      }}
    >
      <Lock className="size-2.5" strokeWidth={2.5} />
    </span>
  );
};

const getCategoryTooltipText = (
  category: UpsellCategoryKey | undefined,
  upsellPromotion: Partial<Record<UpsellCategoryKey, UpsellMetadata>> | undefined,
): string | null => {
  return getUpsellMetadata(category, upsellPromotion)?.tooltip ?? null;
};

const Navigation = ({ projects }: { projects: NavigationItem[] }) => {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const { plan } = useOrganizationPlan(false);
  const upsellPromotion = plan?.upsell_promotion;
  const location = useLocation();
  const navigate = useNavigate();
  const { counts } = useAdminNotificationsContext();

  // Chat unread is tracked client-side via React Query (Pusher-driven),
  // not part of the admin notifications endpoint. Only fetch when the user
  // actually has chat access — checked via the navigation tree. Memoized
  // so a parent re-render with a new `projects` reference but identical
  // chat presence doesn't toggle the query's `enabled` flag.
  const hasChatItem = useMemo(
    () => projects.some((p) =>
      p.name === "Chat" || p.items?.some((s) => s.name === "Chat"),
    ),
    [projects],
  );
  const chatUnread = useChatUnreadCount({ enabled: hasChatItem });

  // In-progress jobs are tracked client-side via Pusher (jobDataManager), not
  // the admin notifications endpoint — same case as chat unread below.
  const jobsInProgress = useJobCounter();

  /** Render a single summed badge for a parent item (own count + children counts) */
  const renderParentCounter = (item: NavigationItem) => {
    const seenKeys = new Set<string>();
    let total = 0;

    const ownKey = COUNT_KEY_MAP[item.name];
    if (ownKey) {
      seenKeys.add(ownKey);
      total += counts[ownKey] ?? 0;
    }

    item.items?.forEach((sub) => {
      const childKey = COUNT_KEY_MAP[sub.name];
      if (childKey && !seenKeys.has(childKey)) {
        seenKeys.add(childKey);
        total += counts[childKey] ?? 0;
      }
    });

    // Chat doesn't have a server-side count key — fold it in directly
    // when the parent contains a Chat sub-item (e.g. "Communication").
    const includesChat = item.name === "Chat"
      || item.items?.some((sub) => sub.name === "Chat");
    if (includesChat) total += chatUnread;

    // "Tâches" is push-driven too (no server count key) — fold it into the
    // parent sum when the parent contains the Tâches sub-item (e.g. "Outils").
    const includesJobs = item.name === "Tâches"
      || item.items?.some((sub) => sub.name === "Tâches");
    if (includesJobs) total += jobsInProgress;

    // "À modérer" = pending + reported, so the Petites Annonces badge folds in reported too
    // (COUNT_KEY_MAP already contributed classified_ads_pending above).
    if (item.name === "Petites Annonces") total += counts.classified_ads_reported;

    return <NotificationBadge count={total} />;
  };

  const wrapWithTooltip = (node: React.ReactElement, tooltipText: string | null) => {
    if (!tooltipText) return node;
    return (
      <Tooltip key={node.key ?? undefined}>
        <TooltipTrigger asChild>{node}</TooltipTrigger>
        <TooltipContent side="right">{tooltipText}</TooltipContent>
      </Tooltip>
    );
  };

  // États locaux pour chaque accordéon
  const [openAccordions, setOpenAccordions] = useState<Record<string, boolean>>({});

  // Match URL d'item de nav contre la location courante. Quand l'URL
  // contient un query string (ex. sidebar « Réservations <collection> »
  // avec `?category=Livres`), on vérifie que CHAQUE param attendu est
  // présent dans `location.search` — sinon deux items de la même page
  // se considéreraient mutuellement actifs.
  const isActiveRoute = (url: string) => {
    if (url === "/") {
      return location.pathname === url;
    }
    const [pathPart, queryPart] = url.split("?");
    const pathMatches
      = location.pathname.startsWith(pathPart)
        && (location.pathname === pathPart || location.pathname.charAt(pathPart.length) === "/");
    if (!pathMatches) return false;
    if (!queryPart) return true;
    const expected = new URLSearchParams(queryPart);
    const current = new URLSearchParams(location.search);
    for (const [k, v] of expected) {
      if (current.get(k) !== v) return false;
    }
    return true;
  };

  const isSubItemActive = (subItem: NavigationSubItem, siblings: NavigationSubItem[]) => {
    const matchesPrimary = isActiveRoute(subItem.url);
    const matchesExtra = subItem.activePaths?.some((p) => isActiveRoute(p)) ?? false;
    if (!matchesPrimary && !matchesExtra) return false;
    return !siblings.some(
      (sibling) =>
        sibling.url !== subItem.url
        && sibling.url.length > subItem.url.length
        && (isActiveRoute(sibling.url) || sibling.activePaths?.some((p) => isActiveRoute(p))),
    );
  };

  const toggleAccordion = (itemName: string) => {
    setOpenAccordions((prev) => ({
      ...prev,
      [itemName]: !prev[itemName],
    }));
  };

  const isAccordionOpen = (itemName: string) => {
    // Si l'accordéon a été explicitement ouvert/fermé par l'utilisateur
    if (Object.prototype.hasOwnProperty.call(openAccordions, itemName)) {
      return openAccordions[itemName];
    }

    // Sinon, déterminer l'état initial basé sur les routes actives
    if (itemName === "Gestionnaire de fichiers") {
      return isActiveRoute(FILE_MANAGER_PATH);
    }

    // Pour les autres accordéons, vérifier si une sous-route est active
    const item = projects.find((p) => p.name === itemName);
    if (item?.items) {
      return item.items.some((subItem) =>
        isActiveRoute(subItem.url) || subItem.activePaths?.some((p) => isActiveRoute(p)),
      );
    }

    return false;
  };

  const renderCollapsedSubItems = (item: NavigationItem) => {
    if (item.hasCustomSubContent) return null;
    return item.items?.map((subItem) => {
      const isActive = isSubItemActive(subItem, item.items ?? []);
      const isLocked = !!subItem.locked;
      const IconComponent = subItem.icon || item.icon;
      return (
        <SidebarMenuItem key={`${item.name}-${subItem.name}`}>
          <SidebarMenuButton
            asChild
            tooltip={isLocked ? `${subItem.name} (PRO)` : subItem.name}
          >
            <Link
              to={subItem.url}
              className={cn(
                isActive
                  ? "bg-blue-500 text-white font-medium hover:!bg-blue-500 hover:!text-white transition-colors"
                  : "",
                isLocked && LOCKED_CONTENT_CLASS,
              )}
            >
              <IconComponent />
            </Link>
          </SidebarMenuButton>
          {subItem.name === "Tâches" && (
            <JobFloatingIndicator className="pointer-events-none absolute right-1 top-1 z-10" />
          )}
        </SidebarMenuItem>
      );
    });
  };

  const renderFileManagerItem = (item: NavigationItem) => {
    if (item.locked) {
      return renderSimpleItem(item);
    }

    const isOpen = isAccordionOpen(item.name);
    const isActive = isActiveRoute(item.url);
    const tooltipText = getCategoryTooltipText(item.upsellCategory, upsellPromotion);
    const node = (
      <div key={item.name} className="group/collapsible">
        <SidebarMenuItem>
          <SidebarMenuButton
            tooltip={collapsed ? item.name : undefined}
            onClick={(e) => {
              const target = e.target as HTMLElement;
              const isChevronClick = target.closest("[data-chevron]") || target.hasAttribute("data-chevron");

              if (!isChevronClick) {
                e.preventDefault();
                e.stopPropagation();
                navigate(item.url);
              }
              else {
                toggleAccordion(item.name);
              }
            }}
            className={
              isActive
                ? "bg-primary text-white font-medium hover:!bg-primary hover:!text-white transition-colors"
                : ""
            }
          >
            <item.icon />
            {!collapsed && <span>{item.name}</span>}
            {renderParentCounter(item)}
            {!collapsed && (
              <ChevronRight
                data-chevron
                className={`ml-auto transition-transform duration-200 ${isOpen ? "rotate-90" : "rotate-0"}`}
              />
            )}
          </SidebarMenuButton>
        </SidebarMenuItem>

        {isOpen && (
          <div className="overflow-hidden">
            <FileTreeNavigation />
          </div>
        )}
      </div>
    );
    return wrapWithTooltip(node, !collapsed ? tooltipText : null);
  };

  const renderCollapsibleItem = (item: NavigationItem) => {
    const isLocked = !!item.locked;
    const isOpen = isAccordionOpen(item.name);
    const tooltipText = getCategoryTooltipText(item.upsellCategory, upsellPromotion);

    // Si le parent est entièrement verrouillé (tous enfants locked), naviguer au clic
    if (isLocked) {
      const node = (
        <div key={item.name} className="group/collapsible">
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              tooltip={collapsed ? `${item.name} (PRO)` : undefined}
            >
              <Link
                to={item.url}
                className={LOCKED_CONTENT_CLASS}
              >
                <item.icon />
                {!collapsed && <span className="truncate">{item.name}</span>}
                {!collapsed && <ProBadge tier={item.requiredTier} />}
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </div>
      );
      return wrapWithTooltip(node, !collapsed ? tooltipText : null);
    }

    const node = (
      <div key={item.name} className="group/collapsible">
        <SidebarMenuItem>
          <SidebarMenuButton
            tooltip={collapsed ? item.name : undefined}
            onClick={() => toggleAccordion(item.name)}
          >
            <item.icon />
            {!collapsed && <span>{item.name}</span>}
            {renderParentCounter(item)}
            {!collapsed && (
              <ChevronRight className={`ml-auto transition-transform duration-200 ${isOpen ? "rotate-90" : "rotate-0"}`} />
            )}
          </SidebarMenuButton>
          {!isOpen && parentHasJobSubItem(item) && (
            <JobFloatingIndicator className="pointer-events-none absolute left-5 top-1.5 z-10" />
          )}
        </SidebarMenuItem>

        {isOpen && (
          <div className="overflow-hidden">
            <SidebarMenuSub>
              {item.items?.map((subItem) => {
                const isActive = isSubItemActive(subItem, item.items ?? []);
                const isSubLocked = !!subItem.locked;
                return (
                  <SidebarMenuSubItem key={subItem.name}>
                    <SidebarMenuSubButton asChild>
                      <Link
                        to={subItem.url}
                        className={cn(
                          isActive
                            ? "bg-blue-500 text-white font-medium hover:!bg-blue-500 hover:!text-white transition-colors"
                            : "",
                          isSubLocked && LOCKED_CONTENT_CLASS,
                        )}
                      >
                        <TruncatedSidebarLabel text={subItem.name} />
                        {isSubLocked && <ProBadge tier={subItem.requiredTier} />}
                        {renderSubItemCounter(subItem)}
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                );
              })}
            </SidebarMenuSub>
          </div>
        )}
      </div>
    );
    return wrapWithTooltip(node, !collapsed ? tooltipText : null);
  };

  const renderSimpleItem = (item: NavigationItem) => {
    const isActive = isActiveRoute(item.url);
    const isLocked = !!item.locked;
    const tooltipText = getCategoryTooltipText(item.upsellCategory, upsellPromotion);
    const node = (
      <SidebarMenuItem key={item.name}>
        <SidebarMenuButton
          asChild
          tooltip={collapsed ? (isLocked ? `${item.name} (PRO)` : item.name) : undefined}
        >
          <Link
            to={item.url}
            className={cn(
              isActive
                ? "bg-primary text-white font-medium hover:!bg-primary hover:!text-white transition-colors"
                : "",
              isLocked && LOCKED_CONTENT_CLASS,
            )}
          >
            <item.icon />
            {!collapsed && <span className={cn(isLocked && "truncate")}>{item.name}</span>}
            {!collapsed && isLocked && <ProBadge tier={item.requiredTier} />}
            {!isLocked && renderParentCounter(item)}
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
    return wrapWithTooltip(node, !collapsed ? tooltipText : null);
  };

  return (
    <SidebarGroup>
      {!collapsed && <SidebarGroupLabel>Navigation</SidebarGroupLabel>}
      <SidebarMenu>
        {projects.map((item) => {
          if ((item.items && item.items.length > 0) || item.hasCustomSubContent) {
            if (collapsed) {
              if (item.locked) {
                return (
                  <SidebarMenuItem key={item.name}>
                    <SidebarMenuButton
                      asChild
                      tooltip={`${item.name} (PRO)`}
                    >
                      <Link
                        to={item.url}
                        className={LOCKED_CONTENT_CLASS}
                      >
                        <item.icon />
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              }
              return renderCollapsedSubItems(item);
            }

            // Pour "Gestionnaire de fichiers", utiliser une structure spéciale
            if (item.hasCustomSubContent && item.url === FILE_MANAGER_PATH) {
              return renderFileManagerItem(item);
            }

            // Pour les autres éléments collapsibles, utiliser la structure normale
            return renderCollapsibleItem(item);
          }

          return renderSimpleItem(item);
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
};

export default Navigation;
